import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { startFocusTrail } from './lib/focusTrail';

// Walter 2026-11-03: record clicks that ask for a caret and don't get one
// (#255). Two passive document listeners, installed before React mounts so
// nothing can happen unrecorded. Log-only — see lib/focusTrail.js.
startFocusTrail();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<React.StrictMode><App /></React.StrictMode>);

// Walter 2026-05-15: register the service worker eagerly on boot so the
// PWA shell + offline caching work for every visitor — not just users
// who've opted in to push notifications (which used to be the only path
// to registering /reminder-sw.js, see hooks/usePushSubscription.js).
//
// Registration is idempotent; the push-subscription hook still calls
// register() too, and the browser returns the existing registration.
//
// The SW only activates after the page finishes loading to avoid
// competing with the React boot for bandwidth on slow connections.
//
// Walter 2026-05-16: auto-update wiring. iOS Safari PWAs in
// particular cache aggressively — a redeploy can take ~24h to reach
// installed users without explicit intervention. The standard PWA
// pattern: on register, listen for `updatefound`; when the new SW
// reaches `activated` AND there's an existing controller (i.e. this
// is a real UPDATE, not the first install), reload the page so the
// user picks up the new bundle. Plus an `update()` call on
// visibilitychange so opening the app from background triggers a
// re-check (rather than waiting for iOS's once-a-day SW check).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Walter 2026-06-11: ask the browser to KEEP our offline cache from
    // being evicted under storage pressure / disuse — the main lever for
    // "offline still works after the phone's sat idle a while" (iOS evicts
    // PWA caches aggressively). Best-effort: Android Chrome honours
    // persist(); iOS may not, but it costs nothing to ask.
    if (navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().catch(() => { /* non-fatal */ });
    }

    navigator.serviceWorker.register('/reminder-sw.js').then((reg) => {
      // Guard against double-reload if the SW activates more than once
      // in the same page lifetime (shouldn't happen, but cheap insurance).
      let reloaded = false;

      // Walter 2026-06-11: reload to pick up a new version, but NEVER in
      // ways that interrupt the user or break offline:
      //   • not while OFFLINE — reloading could land in a state the SW
      //     can't serve from cache (the "can't use offline" symptom). The
      //     new SW is already active; the next online load picks it up.
      //   • not while the user is mid-typing — that yanks focus and reads
      //     as the app "insisting on reloading". Retry once they're idle.
      const isEditing = () => {
        const el = document.activeElement;
        return !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || el.isContentEditable);
      };
      const safeReload = () => {
        if (reloaded) return;
        if (!navigator.onLine) return;            // defer to next online load
        if (isEditing()) { setTimeout(safeReload, 4000); return; }
        reloaded = true;
        window.location.reload();
      };

      // Watch for a new SW being installed during this page's lifetime.
      // When it reaches `activated` AND there's an existing controller
      // (a real UPDATE, not first install), reload — safely.
      reg.addEventListener('updatefound', () => {
        const sw = reg.installing;
        if (!sw) return;
        sw.addEventListener('statechange', () => {
          if (sw.state === 'activated' && navigator.serviceWorker.controller) {
            // Tiny delay to let any in-flight save flush before reload.
            setTimeout(safeReload, 250);
          }
        });
      });

      // Trigger an update check when the tab refocuses — but THROTTLED so
      // we don't re-fetch the SW (and risk a reload) on every glance.
      // Catches "came back after a while, is there a new version?" without
      // making the app feel like it reloads constantly.
      let lastUpdateCheck = 0;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'visible') return;
        const now = Date.now();
        if (now - lastUpdateCheck < 30 * 60 * 1000) return; // at most every 30 min
        lastUpdateCheck = now;
        reg.update().catch(() => { /* swallow — non-fatal */ });
      });
    }).catch((err) => {
      // Non-fatal — the app works fine without the SW (no offline, no
      // install prompt on Android). Log and move on.
      console.warn('[sw] registration failed:', err);
    });
  });
}
